import axios, { AxiosRequestConfig } from 'axios';
import { environments } from '@/utils/helpers';
import { tokenDecoder } from '@/utils/jwt';
import { REFRESH_TOKEN, SWITCH_BRANCH } from './endpoints';

/**
 * Oturum anahtarlarının tek sahibi. Yenileme yapan her yol (HTTP
 * interceptor, uygulama açılışı, soket, SSE) buradan geçer; böylece bir
 * sekmede aynı anda tek yenileme isteği çalışır.
 */

export const ACCESS_TOKEN_KEY = 'access_token';
export const REFRESH_TOKEN_KEY = 'refresh_token';
/** Bu sekmede yeni anahtarlar saklandığında gönderilir (diğer sekmeler `storage` olayını alır). */
export const AUTH_TOKENS_CHANGED_EVENT = 'auth-tokens-changed';

/** Her yenileme HTTP çağrısının süre sınırı. */
export const REFRESH_TIMEOUT_MS = 15_000;

export interface SessionTokens {
  accessToken: string;
  refreshToken: string;
}

export class AuthRefreshTimeoutError extends Error {
  constructor() {
    super('Session refresh timed out');
    this.name = 'AuthRefreshTimeoutError';
  }
}

export class SessionExpiredError extends Error {
  constructor() {
    super('Session expired');
    this.name = 'SessionExpiredError';
  }
}

let sessionExpiredHandler: () => void = () => {
  window.location.href = '/auth/login';
};

/** Yenileme 401 veya 403 ile reddedildiğinde çağrılır (giriş sayfasına yönlendirir). */
export function setSessionExpiredHandler(handler: () => void) {
  sessionExpiredHandler = handler;
}

export function readTokens(): {
  accessToken: string | null;
  refreshToken: string | null;
} {
  return {
    accessToken: localStorage.getItem(ACCESS_TOKEN_KEY),
    refreshToken: localStorage.getItem(REFRESH_TOKEN_KEY)
  };
}

export class InvalidSessionTokensError extends Error {
  constructor() {
    super('Session token response is incomplete');
    this.name = 'InvalidSessionTokensError';
  }
}

const isUsableToken = (token: unknown): token is string =>
  typeof token === 'string' && /^\S+$/.test(token);

/**
 * İki anahtarı birlikte doğrular ve birlikte yazar; biri eksikse hiçbiri
 * yazılmaz ve eski oturum yerinde kalır.
 */
export function storeTokens(tokens: Partial<SessionTokens> | null | undefined) {
  const accessToken = tokens?.accessToken;
  const refreshToken = tokens?.refreshToken;
  if (!isUsableToken(accessToken) || !isUsableToken(refreshToken))
    throw new InvalidSessionTokensError();
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  window.dispatchEvent(new Event(AUTH_TOKENS_CHANGED_EVENT));
}

function expireSession() {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  sessionExpiredHandler();
}

const isAuthRejection = (error: unknown) => {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  return status === 401 || status === 403;
};

/**
 * Bir HTTP çağrısını REFRESH_TIMEOUT_MS ile sınırlar; süre dolunca istek
 * iptal edilir ve AuthRefreshTimeoutError atılır. Kilit içindeki her istek
 * bunu kullanır, böylece kilit sonsuza dek tutulamaz.
 */
export async function withRequestTimeout<T>(
  run: (signal: AbortSignal) => Promise<T>
): Promise<T> {
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, REFRESH_TIMEOUT_MS);
  try {
    return await run(controller.signal);
  } catch (error) {
    if (timedOut) throw new AuthRefreshTimeoutError();
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

/** Interceptor içermeyen, süre sınırlı tek bir HTTP çağrısı. */
const sendWithTimeout = <T>(config: AxiosRequestConfig) =>
  withRequestTimeout((signal) =>
    axios.request<T>({
      ...config,
      baseURL: environments.backendBaseUrl,
      signal
    })
  );

/**
 * /auth/refresh çağrısını yapar ve yeni anahtarları saklar. Yalnızca
 * withSessionLock içinden çağrılmalı; dışarıdan refreshSession kullanılır.
 */
export async function refreshSessionWhileLocked(): Promise<string> {
  const { refreshToken } = readTokens();
  if (!refreshToken) {
    expireSession();
    throw new SessionExpiredError();
  }

  let tokens: SessionTokens;
  try {
    const response = await sendWithTimeout<SessionTokens>({
      method: 'get',
      url: REFRESH_TOKEN,
      headers: { Authorization: `Bearer ${refreshToken}` }
    });
    tokens = response.data;
  } catch (error) {
    // Yalnızca 401 veya 403 oturumu kapatır; ağ hatası, 5xx ve süre aşımı
    // geçicidir ve anahtarlar korunur.
    if (isAuthRejection(error)) expireSession();
    throw error;
  }

  storeTokens(tokens);
  return restoreSessionScope(refreshToken, tokens);
}

const scopeOf = (token: string) => {
  const claims = tokenDecoder(token);
  if (claims?.viewMode === 'admin')
    return { branchId: null, viewMode: 'admin' };
  if (typeof claims?.branchId === 'number')
    return { branchId: claims.branchId as number, viewMode: claims.viewMode };
  return null;
};

/** BranchSelector'ın sakladığı kullanıcı seçimi. */
const SELECTED_BRANCH_KEY = 'selected_branch_id';
const VIEW_MODE_KEY = 'view_mode_preference';

/**
 * Sunucu seçili şubeye geri dönüşü reddettiğinde gönderilir; detail.branchId
 * anahtarın taşıdığı şubedir (yönetici görünümünde null).
 */
export const AUTH_BRANCH_RESET_EVENT = 'auth-branch-reset';

/** Kullanıcının arayüzde seçtiği kapsam; seçim yoksa yenileme anahtarındaki. */
const wantedScopeOf = (previousRefreshToken: string) => {
  const viewMode = localStorage.getItem(VIEW_MODE_KEY);
  if (viewMode === 'admin') return { branchId: null, viewMode: 'admin' };
  const selected = Number(localStorage.getItem(SELECTED_BRANCH_KEY));
  if (Number.isInteger(selected) && selected > 0)
    return { branchId: selected, viewMode: 'branch' };
  return scopeOf(previousRefreshToken);
};

let branchResetHandler: () => void = () => {};

/** Seçili şubeye geri dönüş reddedildiğinde kullanıcıya bildirim için. */
export function setBranchResetHandler(handler: () => void) {
  branchResetHandler = handler;
}

/** Arayüz seçimini anahtarın kapsamına çeker ve bunu duyurur. */
function alignSelectionWith(scope: { branchId: number | null }) {
  if (scope.branchId === null) {
    localStorage.removeItem(SELECTED_BRANCH_KEY);
    localStorage.setItem(VIEW_MODE_KEY, 'admin');
  } else {
    localStorage.setItem(SELECTED_BRANCH_KEY, String(scope.branchId));
    localStorage.setItem(VIEW_MODE_KEY, 'branch');
  }
  window.dispatchEvent(
    new CustomEvent(AUTH_BRANCH_RESET_EVENT, {
      detail: { branchId: scope.branchId }
    })
  );
  branchResetHandler();
}

/**
 * Geçici istemci çözümü: backend yenilemede kullanıcının birincil şubesini
 * imzalıyor. Yeni erişim anahtarının şubesi veya görünümü kullanıcının
 * seçtiğinden farklıysa o şubeye (veya yönetici görünümüne) yeniden
 * geçilir. Geçiş geçici olarak başarısız olursa seçim korunur ve sonraki
 * yenileme yeniden dener; sunucu reddederse (401, 403) seçim anahtara
 * çekilir ve kullanıcıya bildirilir. Yetkiler her durumda güncellenir.
 */
async function restoreSessionScope(
  previousRefreshToken: string,
  tokens: SessionTokens
): Promise<string> {
  const wanted = wantedScopeOf(previousRefreshToken);
  const received = scopeOf(tokens.accessToken);
  if (
    !wanted ||
    !received ||
    (wanted.branchId === received.branchId &&
      wanted.viewMode === received.viewMode)
  )
    return tokens.accessToken;

  try {
    const response = await sendWithTimeout<SessionTokens>({
      method: 'post',
      url: SWITCH_BRANCH,
      data: { branchId: wanted.branchId },
      headers: { Authorization: `Bearer ${tokens.accessToken}` }
    });
    storeTokens(response.data);
    return response.data.accessToken;
  } catch (error) {
    console.warn('[auth] Could not restore the branch after refresh:', error);
    if (isAuthRejection(error)) alignSelectionWith(received);
    return tokens.accessToken;
  } finally {
    window.dispatchEvent(new Event('ability-update'));
  }
}

const AUTH_LOCK_NAME = 'uyanik-admin-auth-session';
/** Kilidi tutan sekme en fazla iki HTTP çağrısı yapar (yenileme ve şube düzeltmesi). */
const LOCK_WAIT_TIMEOUT_MS = 2 * REFRESH_TIMEOUT_MS;

let localLockTail: Promise<void> = Promise.resolve();

/** Sayfa içi sıra; bekleme de LOCK_WAIT_TIMEOUT_MS ile sınırlı. */
function withLocalLock<T>(task: () => Promise<T>): Promise<T> {
  const previous = localLockTail;
  let release!: () => void;
  const held = new Promise<void>((resolve) => (release = resolve));
  localLockTail = previous.then(() => held);

  return new Promise<T>((resolve, reject) => {
    let gaveUp = false;
    const timer = setTimeout(() => {
      gaveUp = true;
      release();
      reject(new AuthRefreshTimeoutError());
    }, LOCK_WAIT_TIMEOUT_MS);
    previous.then(() => {
      if (gaveUp) return;
      clearTimeout(timer);
      task().then(resolve, reject).finally(release);
    });
  });
}

/**
 * Oturum anahtarlarını okuyup yazan işleri sekmeler arasında sıraya koyar
 * (Web Locks). Tarayıcı desteklemiyorsa yalnızca bu sayfa içinde sıralar.
 * Kilit yeniden girilemez: işin içinden refreshSession çağrılmamalı.
 */
export function withSessionLock<T>(task: () => Promise<T>): Promise<T> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  if (!locks?.request) return withLocalLock(task);

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), LOCK_WAIT_TIMEOUT_MS);
  return locks
    .request(AUTH_LOCK_NAME, { signal: controller.signal }, () => {
      clearTimeout(timer);
      return task();
    })
    .catch((error) => {
      if (controller.signal.aborted) throw new AuthRefreshTimeoutError();
      throw error;
    })
    .finally(() => clearTimeout(timer));
}

let refreshInFlight: Promise<string> | null = null;

/**
 * Oturumu yeniler ve yeni erişim anahtarını döndürür. Aynı sekmede eşzamanlı
 * çağıranların hepsi aynı sözü bekler. Kilit alındıktan sonra anahtarlar
 * yeniden okunur; başka bir sekme bu arada yenilediyse /auth/refresh
 * çağrılmadan onun anahtarı kullanılır.
 *
 * @param sentAccessToken Başarısız isteğin gönderdiği erişim anahtarı.
 */
export function refreshSession(
  sentAccessToken: string | null = readTokens().accessToken
): Promise<string> {
  if (!refreshInFlight) {
    refreshInFlight = withSessionLock(async () => {
      const { accessToken } = readTokens();
      if (accessToken && accessToken !== sentAccessToken) return accessToken;
      return refreshSessionWhileLocked();
    }).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}
